/**
 * Billboard damage callouts (FE-30 P3).
 *
 * On every `hit` event at the current tick, spawn a Drei `<Billboard>`
 * + `<Text>` at the victim's world position reading `−N <zone>`. The
 * label floats up `CALLOUT_FLOAT_DISTANCE` units and fades over
 * `CALLOUT_DURATION_MS`.
 *
 * Scrubbing semantics: callouts live on wall-clock (≤ 900ms) and are
 * keyed by `(tick, victim, zone, index)` so re-mounting at the same
 * tick doesn't double-spawn. Scrubbing back leaves any in-flight
 * callouts to expire naturally — the worst case is < 1s of stale text.
 *
 * Heavy hits (`damage ≥ CALLOUT_CRIT_THRESHOLD`) get a ⚡ glyph prefix
 * so the reader can spot crits without parsing numbers.
 */

import { useEffect, useRef, useState } from "react";
import { Billboard, Text } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import type { Group } from "three";
import type { BattleState } from "@pilot-protocol/replay-parser";
import {
  type ActiveCallout,
  CALLOUT_DURATION_MS,
  CALLOUT_FONT_SIZE,
  CALLOUT_OUTLINE_WIDTH,
  calloutFloat,
  calloutId,
  calloutOpacity,
  formatCalloutLabel,
} from "./damageCallout";
import { type RenderEvent } from "../replay/events";
import { hexToWorld } from "./hexMath";

const COLOR_DAMAGE_A = "#fb7185"; // rose — damage *to* A
const COLOR_DAMAGE_B = "#fcd34d"; // amber — damage *to* B
const OUTLINE_COLOR = "#000000";

export interface DamageCalloutsProps {
  /** Events fired at the current tick. Filtered for `kind === "hit"`. */
  eventsHere: ReadonlyArray<RenderEvent>;
  /** The engine state at the current tick — used to anchor callouts. */
  state: BattleState;
  /** Current tick — drives the dedupe key. */
  tick: number;
}

export function DamageCallouts({ eventsHere, state, tick }: DamageCalloutsProps) {
  const [callouts, setCallouts] = useState<ActiveCallout[]>([]);
  const seenRef = useRef<Set<string>>(new Set());

  // Spawn callouts for new hit events at this tick. The seen-set
  // dedupes across re-renders at the same tick.
  useEffect(() => {
    const now = performance.now();
    const fresh: ActiveCallout[] = [];
    let hitIndex = 0;
    for (const e of eventsHere) {
      if (e.kind !== "hit") continue;
      const id = calloutId(e.tick, e.victim, e.zone, hitIndex);
      hitIndex++;
      if (seenRef.current.has(id)) continue;
      seenRef.current.add(id);
      const victimMech = e.victim === "A" ? state.mech_a : state.mech_b;
      const w = hexToWorld(victimMech.position, 0);
      fresh.push({
        id,
        tick: e.tick,
        victim: e.victim,
        label: formatCalloutLabel(e.damage, e.zone),
        origin: [w.x, 1.0, w.z],
        startedAt: now,
      });
    }
    if (fresh.length > 0) {
      setCallouts((prev) => [...prev, ...fresh]);
    }
  }, [eventsHere, state, tick]);

  // Garbage-collect expired callouts on a heartbeat. We only walk the
  // list once per frame; rendering the callouts themselves reads their
  // current state imperatively (refs) so this won't thrash React.
  useFrame(() => {
    if (callouts.length === 0) return;
    const now = performance.now();
    const live = callouts.filter((c) => now - c.startedAt < CALLOUT_DURATION_MS);
    if (live.length !== callouts.length) setCallouts(live);
  });

  if (callouts.length === 0) return null;

  return (
    <group name="damage-callouts">
      {callouts.map((c) => (
        <CalloutLabel key={c.id} callout={c} />
      ))}
    </group>
  );
}

interface CalloutLabelProps {
  callout: ActiveCallout;
}

function CalloutLabel({ callout }: CalloutLabelProps) {
  const groupRef = useRef<Group>(null);
  const baseY = callout.origin[1];

  useFrame(() => {
    const g = groupRef.current;
    if (!g) return;
    const elapsed = performance.now() - callout.startedAt;
    g.position.y = baseY + calloutFloat(elapsed);
    // Cascade opacity onto the Text material via the Drei mesh.
    const opacity = calloutOpacity(elapsed);
    g.traverse((obj) => {
      const m = (obj as unknown as { material?: { opacity?: number; transparent?: boolean } })
        .material;
      if (m && typeof m.opacity === "number") {
        m.opacity = opacity;
        m.transparent = true;
      }
    });
  });

  const color = callout.victim === "A" ? COLOR_DAMAGE_A : COLOR_DAMAGE_B;

  return (
    <group ref={groupRef} position={callout.origin}>
      <Billboard>
        <Text
          fontSize={CALLOUT_FONT_SIZE}
          color={color}
          outlineWidth={CALLOUT_OUTLINE_WIDTH}
          outlineColor={OUTLINE_COLOR}
          anchorX="center"
          anchorY="middle"
        >
          {callout.label}
        </Text>
      </Billboard>
    </group>
  );
}
